import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { Spinner } from '@/components/ui';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { PermissionRequirement } from '@/lib/auth/permissions';
import { ForbiddenPage } from '@/features/errors/ErrorPages';
import { safeReturnTo } from '@/lib/auth/returnTo';
import styles from './app.module.css';

export function FullPageSpinner({ label = 'Loading IntellSchools…' }: { label?: string }) {
  return (
    <div className={styles.fullPage}>
      <Spinner size="lg" label={label} />
    </div>
  );
}

/** Authenticated area: unauthenticated visitors are sent to sign-in and returned afterwards. */
export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'anonymous') {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  return <Outlet />;
}

/** Sign-in pages: already-authenticated users go to their destination. */
export function PublicOnly() {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'authenticated') {
    return (
      <Navigate to={safeReturnTo(new URLSearchParams(location.search).get('returnTo'))} replace />
    );
  }
  return <Outlet />;
}

/**
 * Route-level authorization. Unauthorized routes are not merely hidden from navigation: rendering
 * them shows a 403 page, and the API independently rejects every underlying request.
 */
export function RequireAccess({
  requires,
  children,
}: {
  requires?: PermissionRequirement;
  children: ReactNode;
}) {
  const { satisfies } = useAuth();
  if (!satisfies(requires)) return <ForbiddenPage />;
  return <>{children}</>;
}
